"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface SelectChangeEvent {
  target: {
    value: string;
    name: string;
  };
  currentTarget: {
    value: string;
    name: string;
  };
  value: string;
}

export interface SelectProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "size" | "prefix" | "onChange"
  > {
  label?: React.ReactNode;
  error?: string;
  helperText?: string;
  selectClassName?: string;
  options?: SelectOption[];
  placeholder?: string;
  size?: "sm" | "DEFAULT" | "lg" | "xl";
  searchable?: boolean;
  clearable?: boolean;
  modal?: boolean;
  prefix?: React.ReactNode;
  usePortal?: boolean;
  displayValue?: (
    selected: string | number | readonly string[] | undefined,
  ) => React.ReactNode;
  dropdownClassName?: string;
  getOptionValue?: (option: SelectOption) => string | number;
  onChange?: (
    event: SelectChangeEvent | React.ChangeEvent<HTMLSelectElement>,
  ) => void;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      selectClassName,
      label,
      error,
      helperText,
      options = [],
      placeholder,
      disabled,
      size = "DEFAULT",
      searchable = false,
      clearable = false,
      modal = false,
      usePortal = true,
      prefix,
      displayValue,
      dropdownClassName,
      getOptionValue,
      children,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const selectId = React.useId();
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const [dropdownPosition, setDropdownPosition] = React.useState<{
      top?: number;
      bottom?: number;
      left: number;
      width: number;
      maxHeight?: number;
    } | null>(null);

    // `modal={false}` forces the dropdown to render inline so it lives inside
    // the parent DOM tree (e.g. inside a Radix Dialog). This keeps focus
    // management natural — FocusScope won't try to restore focus back into
    // the dialog when the user clicks the search input.
    const shouldUsePortal = usePortal && modal !== false;

    const updatePosition = React.useCallback(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const maxDropdownHeight = 320;
        const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;

        if (openUpward) {
          setDropdownPosition({
            bottom: window.innerHeight - rect.top + 4,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(120, Math.min(maxDropdownHeight, spaceAbove - 16)),
          });
        } else {
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(120, Math.min(maxDropdownHeight, spaceBelow - 16)),
          });
        }
      }
    }, []);

    const sizeClasses = {
      sm: "h-8 text-xs",
      DEFAULT: "h-10 text-sm",
      lg: "h-11 text-base",
      xl: "h-12 text-base",
    };

    // Close dropdown when clicking outside or update position on scroll
    React.useEffect(() => {
      if (!searchable) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const inContainer = containerRef.current?.contains(target);
        const inDropdown =
          dropdownRef.current?.contains(target) ||
          (target as HTMLElement)?.closest?.(
            "[data-select-dropdown-portal]",
          ) ||
          (target as HTMLElement)?.closest?.("[data-select-dropdown]");
        if (!inContainer && !inDropdown) {
          setIsOpen(false);
          setSearchQuery("");
          setDropdownPosition(null);
        }
      };

      const handleScrollOrResize = (event: Event) => {
        if (!isOpen) return;

        const target = event.target as HTMLElement | null;
        if (
          event.type === "scroll" &&
          target &&
          typeof target.closest === "function"
        ) {
          if (target.closest("[data-select-dropdown-portal]")) return;
          if (target.closest("[data-select-dropdown]")) return;
          if (containerRef.current?.contains(target)) return;
          
          updatePosition();
          return;
        }

        updatePosition();
      };

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }, [searchable, isOpen]);

    // Filter options based on search query
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery || !searchable) return options;
      const q = searchQuery.toLowerCase();
      return options.filter((option) => {
        const label =
          typeof option.label === "string"
            ? option.label
            : String(option.label ?? "");
        return label.toLowerCase().includes(q);
      });
    }, [options, searchQuery, searchable]);

    // Focus the search input shortly after the dropdown mounts so the user
    // can start typing immediately. Defer to next tick so the portal/inline
    // node is committed before we try to focus.
    React.useEffect(() => {
      if (!isOpen || !searchable) return;
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }, [isOpen, searchable]);

    // Helper to safely extract string value even if an event or object is passed as value
    const getRawValueStr = React.useCallback((val: any): string => {
      if (val === undefined || val === null) return "";
      if (typeof val === "object") {
        if (val.target && typeof val.target.value !== "undefined") return String(val.target.value);
        if (typeof val.value !== "undefined") return String(val.value);
      }
      return String(val);
    }, []);

    const rawValueString = getRawValueStr(value);

    // Get selected option to display label
    const selectedOption = React.useMemo(() => {
      if (rawValueString === "") return null;
      if (!options || options.length === 0) return null;

      const found = options.find((option) => {
        const optionValue = getOptionValue ? getOptionValue(option) : option.value;
        const normalizedOptionValue = typeof optionValue === 'object' ? String(optionValue) : String(optionValue);
        return normalizedOptionValue === rawValueString;
      });

      return found;
    }, [options, rawValueString, getOptionValue]);

    // Ensure value exists in options - if not, treat as empty string
    const safeValue = React.useMemo(() => {
      if (rawValueString === "") return "";
      if (!options || options.length === 0) return "";
      const exists = options.some((option) => {
        const optionValue = getOptionValue ? getOptionValue(option) : option.value;
        return String(optionValue) === rawValueString;
      });
      return exists ? rawValueString : "";
    }, [options, rawValueString, getOptionValue]);

    // Handle option selection
    const handleSelectOption = (option: SelectOption) => {
      const optionValue = getOptionValue
        ? getOptionValue(option)
        : option.value;
      const stringValue = String(optionValue);

      // Explicitly call onChange if it exists, since React ignores native change events
      if (onChange) {
        const name = props.name || "";
        onChange({
          target: { value: stringValue, name },
          currentTarget: { value: stringValue, name },
          value: stringValue,
        } as SelectChangeEvent);
      } else {
        // Fallback to native select element update if no onChange is provided
        const nativeSelect = containerRef.current?.querySelector('select');
        if (nativeSelect) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value")?.set;
          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(nativeSelect, stringValue);
          } else {
            nativeSelect.value = stringValue;
          }
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      setIsOpen(false);
      setSearchQuery("");
      setDropdownPosition(null);
    };

    // Handle clear selection
    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (onChange && clearable) {
        const name = props.name || "";
        onChange({
          target: { value: "", name },
          currentTarget: { value: "", name },
          value: "",
        } as SelectChangeEvent);
      } else {
        const nativeSelect = containerRef.current?.querySelector('select');
        if (nativeSelect) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value")?.set;
          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(nativeSelect, "");
          } else {
            nativeSelect.value = "";
          }
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      setIsOpen(false);
      setSearchQuery("");
      setDropdownPosition(null);
    };

    // If searchable, render custom dropdown
    if (searchable) {
      return (
        <div className={cn("w-full", className)} ref={containerRef}>
          {label && (
            <label
              htmlFor={selectId}
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              {label}
              {props.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <div className="relative">
            {/* Custom Select Display */}
            <div
              className={cn(
                "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 cursor-pointer flex items-center gap-2",
                "focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent",
                "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50",
                error && "border-red-500 focus-within:ring-red-500",
                sizeClasses[size],
                selectClassName,
              )}
              onClick={() => {
                if (disabled) return;
                if (isOpen) {
                  setIsOpen(false);
                  setDropdownPosition(null);
                  return;
                }
                updatePosition();
                setIsOpen(true);
              }}
            >
              {prefix && <div className="flex-shrink-0">{prefix}</div>}
              <div className="flex-1 flex items-center justify-between overflow-hidden">
                <span
                  className={cn("truncate", !selectedOption && "text-gray-400")}
                >
                  {displayValue ? (
                    displayValue(safeValue)
                  ) : selectedOption ? (
                    typeof selectedOption.label === 'string'
                      ? selectedOption.label
                      : String(selectedOption.label)
                  ) : safeValue && options.length === 0 ? (
                    <span className="text-gray-400">Loading...</span>
                  ) : safeValue && !selectedOption ? (
                    <span className="text-red-400">Invalid selection</span>
                  ) : (
                    placeholder || "Select..."
                  )}
                </span>
                <div className="flex items-center gap-1">
                  {clearable && selectedOption && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="hover:bg-gray-200 rounded p-1 transition-colors"
                      disabled={disabled}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-4 h-4 text-gray-400"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className={cn(
                      "w-4 h-4 text-gray-400 transition-transform",
                      isOpen && "rotate-180",
                    )}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && !disabled && (
              !shouldUsePortal ? (
                <div
                  ref={dropdownRef}
                  data-select-dropdown
                  className={cn(
                    "absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden flex flex-col max-h-60",
                    dropdownClassName,
                  )}
                  style={{ top: "100%", left: 0 }}
                >
                  {/* Search Input */}
                  <div className="p-2 border-b border-gray-200 flex-shrink-0">
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          if (filteredOptions.length > 0) {
                            handleSelectOption(filteredOptions[0]);
                          }
                        } else if (e.key === "Escape") {
                          e.stopPropagation();
                          setIsOpen(false);
                          setSearchQuery("");
                          setDropdownPosition(null);
                        }
                      }}
                    />
                  </div>

                  {/* Options List */}
                  <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">
                        No options found
                      </div>
                    ) : (
                      filteredOptions.map((option) => {
                        const optionValue = getOptionValue
                          ? getOptionValue(option)
                          : option.value;
                        const isSelected =
                          String(optionValue) === rawValueString;
                        return (
                            <div
                              key={String(optionValue)}
                              className={cn(
                                "px-3 py-2 text-sm cursor-pointer hover:bg-gray-100",
                                isSelected &&
                                  "bg-blue-50 text-blue-700 font-medium",
                                option.disabled &&
                                  "opacity-50 cursor-not-allowed",
                              )}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                if (!option.disabled) {
                                  handleSelectOption(option);
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!option.disabled) {
                                  handleSelectOption(option);
                                }
                              }}
                            >
                              {option.label}
                            </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                typeof document !== "undefined" &&
                dropdownPosition &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    data-select-dropdown-portal
                    style={{
                      position: "fixed",
                      top: dropdownPosition.top !== undefined ? dropdownPosition.top : "auto",
                      bottom: dropdownPosition.bottom !== undefined ? dropdownPosition.bottom : "auto",
                      left: dropdownPosition.left,
                      width: dropdownPosition.width,
                      maxHeight: dropdownPosition.maxHeight || 320,
                      zIndex: 99999,
                    }}
                    className={cn(
                      "bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden flex flex-col",
                      dropdownClassName,
                    )}
                  >
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200 flex-shrink-0">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            if (filteredOptions.length > 0) {
                              handleSelectOption(filteredOptions[0]);
                            }
                          } else if (e.key === "Escape") {
                            e.stopPropagation();
                            setIsOpen(false);
                            setSearchQuery("");
                            setDropdownPosition(null);
                          }
                        }}
                      />
                    </div>

                    {/* Options List */}
                    <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain">
                      {filteredOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No options found
                        </div>
                      ) : (
                        filteredOptions.map((option) => {
                          const optionValue = getOptionValue
                            ? getOptionValue(option)
                            : option.value;
                          const isSelected =
                            String(optionValue) === rawValueString;
                          return (
                            <div
                              key={String(optionValue)}
                              className={cn(
                                "px-3 py-2 text-sm cursor-pointer hover:bg-gray-100",
                                isSelected &&
                                  "bg-blue-50 text-blue-700 font-medium",
                                option.disabled &&
                                  "opacity-50 cursor-not-allowed",
                              )}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                if (!option.disabled) {
                                  handleSelectOption(option);
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!option.disabled) {
                                  handleSelectOption(option);
                                }
                              }}
                            >
                              {option.label}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>,
                  document.body,
                )
              )
            )}

            {/* Hidden Native Select (for form compatibility) */}
            <select
              id={selectId}
              ref={ref}
              value={value}
              onChange={onChange}
              disabled={disabled}
              className="sr-only"
              {...props}
            >
              {placeholder && (
                <option value="" disabled>
                  {placeholder}
                </option>
              )}
              {options.map((option) => {
                const optionValue = getOptionValue
                  ? getOptionValue(option)
                  : option.value;
                return (
                  <option
                    key={option.value}
                    value={optionValue}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                );
              })}
            </select>
          </div>
          {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
          {helperText && !error && (
            <p className="mt-1.5 text-xs text-gray-500">{helperText}</p>
          )}
        </div>
      );
    }

    // Default: render native select
    return (
      <div className={cn("w-full", className)}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-900 mb-1.5"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative flex items-center">
          {prefix && (
            <div className="absolute left-3 pointer-events-none">{prefix}</div>
          )}
          <select
            id={selectId}
            className={cn(
              "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none bg-none",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50",
              error && "border-red-500 focus:ring-red-500",
              sizeClasses[size],
              prefix && "pl-9",
              selectClassName,
            )}
            ref={ref}
            disabled={disabled}
            value={value}
            onChange={onChange}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {children
              ? children
              : options.map((option) => {
                const optionValue = getOptionValue
                  ? getOptionValue(option)
                  : option.value;
                return (
                  <option
                    key={option.value}
                    value={optionValue}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                );
              })}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </div>
        </div>
        {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
        {helperText && !error && (
          <p className="mt-1.5 text-xs text-gray-500">{helperText}</p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export { Select };
